// Hook React pour la gestion WebSocket de la messagerie en temps réel
import { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config/api';

const WS_RECONNECT_DELAY = 3000; // 3 secondes
const WS_PING_INTERVAL = 30000; // 30 secondes

export const useMessagingWebSocket = (onMessage, onTyping, onReadReceipt) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const mountedRef = useRef(true);

  const token = localStorage.getItem('ecosystem_token');
  const wsUrl = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

  const connect = useCallback(() => {
    if (!token || !wsUrl || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(`${wsUrl}/api/messages/ws/${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        console.log('[WS] Connected to messaging server');
        setIsConnected(true);
        setConnectionError(null);

        // Démarrer le ping pour maintenir la connexion
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'new_message':
              onMessage?.(data);
              break;
            case 'typing':
              onTyping?.(data);
              break;
            case 'read_receipt':
              onReadReceipt?.(data);
              break;
            case 'pong':
              // Réponse au ping, connexion vivante
              break;
            default:
              console.log('[WS] Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('[WS] Error parsing message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] WebSocket error:', error);
        setConnectionError('Erreur de connexion');
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        console.log('[WS] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Nettoyer le ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Reconnexion automatique sauf si fermeture volontaire (code 1000) ou auth error (4001)
        if (event.code !== 1000 && event.code !== 4001) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              console.log('[WS] Attempting reconnection...');
              connect();
            }
          }, WS_RECONNECT_DELAY);
        }
      };
    } catch (error) {
      console.error('[WS] Failed to create WebSocket:', error);
      setConnectionError('Impossible de se connecter');
    }
  }, [token, wsUrl, onMessage, onTyping, onReadReceipt]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect');
      wsRef.current = null;
    }
  }, []);

  // Envoyer un indicateur "en train d'écrire"
  const sendTypingIndicator = useCallback((conversationId, isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: conversationId,
        is_typing: isTyping
      }));
    }
  }, []);

  // Marquer des messages comme lus
  const markMessagesAsRead = useCallback((conversationId, messageIds) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && messageIds.length > 0) {
      wsRef.current.send(JSON.stringify({
        type: 'mark_read',
        conversation_id: conversationId,
        message_ids: messageIds
      }));
    }
  }, []);

  // Connexion initiale
  useEffect(() => {
    mountedRef.current = true;
    if (token) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [token, connect, disconnect]);

  return {
    isConnected,
    connectionError,
    sendTypingIndicator,
    markMessagesAsRead,
    reconnect: connect,
    disconnect
  };
};

export default useMessagingWebSocket;
